import { Flex, Tag, TagCloseButton, TagLabel } from '@chakra-ui/react';
import {
  ComplianceDocumentDefinition,
  ComplianceDocumentType,
} from '@texas/api/endpoints/metadata/complianceDocumentDefinitionsApi';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import {
  complianceDocumentKindLabel,
  complianceDocumentTypeLabel,
} from '@texas/components/configuration/complianceDocumentDefinitions/translations';
import { certificateSelectStyles as styles } from '@texas/resources/theme/styles/entityWorkspace';
import { compareEnum } from '@texas/utils/helpers/enumHelpers';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  branchName: string;
  definitions: ComplianceDocumentDefinition[];
  value: number[];
  onChange: (ids: number[]) => void;
  isDisabled?: boolean;
}

export function CertificateConfigurationSelect({
  branchName,
  definitions,
  value,
  onChange,
  isDisabled = false,
}: Props) {
  const { t } = useTranslation();
  const options = useMemo(
    () =>
      definitions
        .filter(
          (definition) =>
            !definition.archived && !value.includes(definition.id),
        )
        .map((definition) => {
          const kindLabel = complianceDocumentKindLabel(definition.kind, t);
          const typeLabel = complianceDocumentTypeLabel(definition.type, t);
          return {
            value: definition.id,
            label: `${definition.name} · ${kindLabel} · ${typeLabel}`,
          };
        }),
    [definitions, t, value],
  );
  const selectedDefinitions = definitions.filter((definition) =>
    value.includes(definition.id),
  );

  return (
    <Flex sx={styles.selection}>
      <Flex sx={styles.selectedTags}>
        {selectedDefinitions.map((definition) => (
          <Tag
            key={definition.id}
            sx={
              compareEnum(
                ComplianceDocumentType,
                definition.type,
                ComplianceDocumentType.Transactional,
              )
                ? styles.transactionalTag
                : styles.scopedTag
            }
          >
            <TagLabel sx={styles.tagLabel}>
              {definition.name} ·{' '}
              {complianceDocumentKindLabel(definition.kind, t)}
              {' · '}
              {complianceDocumentTypeLabel(definition.type, t)}
              {definition.archived && ` (${t('general.archived')})`}
            </TagLabel>
            <TagCloseButton
              aria-label={`${t('general.remove')} ${definition.name}`}
              isDisabled={isDisabled}
              onClick={() =>
                onChange(value.filter((id) => id !== definition.id))
              }
            />
          </Tag>
        ))}
      </Flex>
      <Flex sx={styles.picker}>
        <TexasSelect
          aria-label={`${t('article.create.certificateLabel')} — ${branchName}`}
          classNamePrefix="branch-certificate-select"
          isClearable={false}
          isDisabled={isDisabled}
          closeMenuOnSelect={false}
          usePortal={true}
          placeholder={t('article.create.addCertificate')}
          options={options}
          value={null}
          onChange={(option) => {
            if (option) {
              onChange([...value, option.value].sort((a, b) => a - b));
            }
          }}
        />
      </Flex>
    </Flex>
  );
}

export function CertificateConfigurationLegend() {
  const { t } = useTranslation();

  return (
    <Flex sx={styles.legend}>
      <Tag sx={styles.scopedTag}>
        {complianceDocumentTypeLabel(ComplianceDocumentType.Scoped, t)}
      </Tag>
      <Tag sx={styles.transactionalTag}>
        {complianceDocumentTypeLabel(ComplianceDocumentType.Transactional, t)}
      </Tag>
    </Flex>
  );
}
